import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import SideBar from "../components/SideBar";
import "../styles/MemberDetails.css";
import { FaRegUserCircle } from "react-icons/fa";
import {
  MdOutlineLocationOn,
  MdUpdate,
  MdOutlineAssignmentInd,
  MdOutlineAdminPanelSettings,
  MdPublic,
} from "react-icons/md";
import { agentService } from "../services/AgentServices";

function AgentDetails() {
  const location = useLocation();
  const agent = location.state?.agent;
  const navigate = useNavigate();

  const [assignedAreas, setAssignedAreas] = useState([]);
  const [assignedFocusCountries, setAssignedFocusCountries] = useState([]);
  const [focusCountryStats, setFocusCountryStats] = useState({
    total: 0,
    primary: 0,
    secondary: 0,
    target: 0
  });
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingFocusCountries, setLoadingFocusCountries] = useState(false);
  const [showAreas, setShowAreas] = useState(false);
  const [showFocusCountries, setShowFocusCountries] = useState(false);

  useEffect(() => {
    // Fetch assigned admin areas if agent is Senior
    if (agent && agent.role === "SeniorBusinessAdsAgent") {
      fetchAssignedAreas();
      fetchAssignedFocusCountries();
    }
  }, [agent]);

  const fetchAssignedAreas = async () => {
    setLoadingAreas(true);
    try {
      const response = await agentService.getAgentAdminAreas(
        agent.agent_id || agent.id,
      );
      if (response.success) {
        setAssignedAreas(response.data.assigned_admin_areas);
      }
    } catch (error) {
      console.error("Error fetching assigned areas:", error);
    } finally {
      setLoadingAreas(false);
    }
  };

  const fetchAssignedFocusCountries = async () => {
    setLoadingFocusCountries(true);
    try {
      const response = await agentService.getAgentFocusCountries(
        agent.agent_id || agent.id
      );
      if (response.success) {
        setAssignedFocusCountries(response.data.focus_countries || []);
        if (response.data.statistics) {
          setFocusCountryStats(response.data.statistics);
        }
      }
    } catch (error) {
      console.error("Error fetching assigned focus countries:", error);
    } finally {
      setLoadingFocusCountries(false);
    }
  };

  if (!agent) {
    return <p style={{ padding: "30px" }}>No more data available.</p>;
  }

  const goToWallet = () => {
    navigate("/agent-wallet-details", { state: { agent } });
  };

  const goToEdit = () => {
    navigate("/edit-agent-profile", { state: { agent } });
  };

  const goToInviteHistory = () => {
    navigate("/agent-invite-history", { state: { agent } });
  };

  const goToPromotion = () => {
    navigate("/agent-promotion", { state: { agent } });
  };

  const toggleAreas = () => {
    setShowAreas(!showAreas);
  };

  const toggleFocusCountries = () => {
    setShowFocusCountries(!showFocusCountries);
  };

  // Get badge color for focus type
  const getFocusTypeBadge = (type) => {
    const colors = {
      primary: { bg: "#e3f2fd", color: "#1976d2", border: "#bbdefb" },
      secondary: { bg: "#e8f5e8", color: "#2e7d32", border: "#c8e6c9" },
      target: { bg: "#fff3e0", color: "#ed6c02", border: "#ffe0b2" }
    };
    return colors[type] || colors.secondary;
  };

  // Check if agent is Senior Business Ads Agent
  const isSeniorAgent = agent.role === "SeniorBusinessAdsAgent";

  return (
    <div className="member-details-layout">
      <SideBar />

      <div className="member-details-main">
        {/* Header */}
        <div className="details-header">
          <h2 style={{ width: "330px" }}>Retail Business Agent Details</h2>
          <p>You can edit your details to update your profile</p>
        </div>

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <Link to="/manage-agent" className="breadcrumb-link">
            Home
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <Link to="/manage-agent" className="breadcrumb-link">
            Manage Retail Advertising Business Agent
          </Link>
          <span className="breadcrumb-separator">{">"}</span>
          <span className="breadcrumb-current">Agent Details</span>
        </div>

        {/* buttons */}
        <div className="top-bar">
          {/* tap buttons */}
          <div className="top-buttons">
            <button className="tab-btn active">Personal Details</button>
            <button className="tab-btn" onClick={goToWallet}>
              Wallet Details
            </button>
            <button className="tab-btn" onClick={goToInviteHistory}>
              Invitation Details
            </button>
            <button className="tab-btn" onClick={goToPromotion}>
              Promote / Demote
            </button>
          </div>
        </div>

        {/* Basic Details */}
        <div className="section">
          {/* edit button */}
          <div className="edit-btn-wrap">
            <button className="edit-btn" onClick={goToEdit}>
              Edit
            </button>
          </div>
          <h3>
            <FaRegUserCircle /> Basic Details
          </h3>
          <div className="section-content">
            <p>
              <strong>First name:</strong> {agent.firstName || agent.first_name}
            </p>
            <p>
              <strong>Surname:</strong> {agent.surname || agent.last_name}
            </p>
            <p>
              <strong>Email:</strong> {agent.email}
            </p>
            <p>
              <strong>Mobile:</strong> {agent.mobile || agent.phone_number}
            </p>
            <p>
              <strong>Role:</strong> {agent.role === "SeniorBusinessAdsAgent" ? "Senior Business Ads Agent" : "Mini Business Ads Agent"}
            </p>
          </div>
        </div>

        {/* Address */}
        <div className="section">
          <h3>
            <MdOutlineLocationOn /> Address
          </h3>
          <div className="section-content">
            <p>
              <strong>Address line 1:</strong> {agent.address?.line1 || agent.address_line1}
            </p>
            <p>
              <strong>Address line 2:</strong> {agent.address?.line2 || agent.address_line2}
            </p>
            <p>
              <strong>Address line 3:</strong> {agent.address?.line3 || agent.address_line3}
            </p>
            <p>
              <strong>City:</strong> {agent.address?.city || agent.city}
            </p>
            <p>
              <strong>Region:</strong> {agent.address?.county || agent.region}
            </p>
            <p>
              <strong>Postcode:</strong> {agent.address?.postcode || agent.postcode}
            </p>
            <p>
              <strong>Country:</strong> {agent.address?.country || agent.country}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="section">
          <h3>
            <MdUpdate /> Status
          </h3>
          <div className="section-content">
            <p>
              <strong>Status:</strong>{" "}
              <span
                style={{
                  color:
                    agent.status === "approved"
                      ? "green"
                      : agent.status === "suspended"
                      ? "orange"
                      : agent.status === "rejected"
                      ? "red"
                      : "blue",
                  fontWeight: "bold",
                }}
              >
                {agent.status?.charAt(0).toUpperCase() + agent.status?.slice(1) || "N/A"}
              </span>
            </p>
            <p>
              <strong>Notes:</strong> {agent.notes || agent.status_note || "N/A"}
            </p>
          </div>
        </div>

        {/* Referred By */}
        <div className="section">
          <h3>
            <MdOutlineAssignmentInd /> Referred By
          </h3>
          <div className="section-content">
            <p>
              <strong>Referrer ID:</strong> {agent.referrer_id || "N/A"}
            </p>
            <p>
              <strong>Referrer:</strong> {agent.referrer_name || "N/A"}
            </p>
            <p>
              <strong>Referrer Agent Type:</strong> {agent.referrer_agent_type || "N/A"}
            </p>
            <p>
              <strong>Referrer Country:</strong>{" "}
              {agent.referrer_country || "N/A"}
            </p>
            <p>
              <strong>Referrer Region:</strong> {agent.referrer_region || "N/A"}
            </p>
            <p>
              <strong>Referrer Email:</strong> {agent.referrer_email || "N/A"}
            </p>
            <p>
              <strong>Referrer Phone:</strong> {agent.referrer_phone || "N/A"}
            </p>
          </div>
        </div>

{/* Admin Areas - Only for Senior Agents */}
        {isSeniorAgent && (
          <div className="section">
            <h3>
              <MdOutlineAdminPanelSettings /> Admin Areas
              {assignedAreas.length > 0 && (
                <button
                  onClick={toggleAreas}
                  style={{
                    marginLeft: "10px",
                    background: "none",
                    border: "1px solid rgba(0, 0, 160, 1)",
                    borderRadius: "15px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "rgba(0, 0, 160, 1)",
                  }}
                >
                  {showAreas ? "Hide" : `View (${assignedAreas.length})`}
                </button>
              )}
            </h3>
            <div className="section-content">
              {loadingAreas ? (
                <p>Loading assigned areas...</p>
              ) : assignedAreas.length === 0 ? (
                <p>
                  <strong>No admin areas assigned</strong>
                </p>
              ) : showAreas ? (
                <div className="admin-areas-list">
                  {assignedAreas.map((area, index) => (
                    <div
                      key={area.admin_area_id}
                      style={{
                        padding: "8px",
                        margin: "5px 0",
                        background: "#f5f5f5",
                        borderRadius: "4px",
                        border: "1px solid #e0e0e0",
                      }}
                    >
                      <p style={{ margin: "2px 0" }}>
                        <strong>{area.admin_name}</strong>
                        {area.county && (
                          <span style={{ color: "#666", marginLeft: "8px" }}>
                            ({area.county.county_name})
                          </span>
                        )}
                      </p>
                      <p
                        style={{
                          margin: "2px 0",
                          fontSize: "12px",
                          color: "#888",
                        }}
                      >
                        Towns: {area.towns_count} | Assigned:{" "}
                        {new Date(area.assigned_at).toLocaleDateString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p>
                  <strong>{assignedAreas.length} admin area(s) assigned</strong>
                </p>
              )}
            </div>
          </div>
        )}
        
        {/* Focus Countries - Only for Senior Agents */}
        {isSeniorAgent && (
          <div className="section">
            <h3>
              <MdPublic /> Focus Countries
              {assignedFocusCountries.length > 0 && (
                <button
                  onClick={toggleFocusCountries}
                  style={{
                    marginLeft: "10px",
                    background: "none",
                    border: "1px solid rgba(0, 0, 160, 1)",
                    borderRadius: "15px",
                    padding: "2px 10px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: "rgba(0, 0, 160, 1)",
                  }}
                >
                  {showFocusCountries ? "Hide" : `View (${assignedFocusCountries.length})`}
                </button>
              )}
            </h3>
            
            {/* Statistics Summary */}
            {assignedFocusCountries.length > 0 && (
              <div
                style={{
                  marginBottom: "15px",
                  background: "#f8f9fa",
                  padding: "10px",
                  borderRadius: "6px",
                  display: "flex",
                  gap: "20px",
                  flexWrap: "wrap",
                }}
              >
                <div>
                  <strong>Total:</strong> {focusCountryStats.total}
                </div>
                <div style={{ color: "#1976d2" }}>
                  <strong>Primary:</strong> {focusCountryStats.primary}
                </div>
                <div style={{ color: "#2e7d32" }}>
                  <strong>Secondary:</strong> {focusCountryStats.secondary}
                </div>
                <div style={{ color: "#ed6c02" }}>
                  <strong>Target:</strong> {focusCountryStats.target}
                </div>
              </div>
            )}

            <div className="section-content">
              {loadingFocusCountries ? (
                <p>Loading assigned focus countries...</p>
              ) : assignedFocusCountries.length === 0 ? (
                <p>
                  <strong>No focus countries assigned</strong>
                </p>
              ) : showFocusCountries ? (
                <div className="admin-areas-list">
                  {assignedFocusCountries.map((country, index) => {
                    const badgeStyle = getFocusTypeBadge(country.focus_type);
                    return (
                      <div
                        key={country.country_id}
                        style={{
                          padding: "12px",
                          margin: "8px 0",
                          background: "#ffffff",
                          borderRadius: "6px",
                          border: "1px solid #e0e0e0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", marginBottom: "6px" }}>
                              <strong style={{ fontSize: "15px" }}>{country.country_name}</strong>
                              <span
                                style={{
                                  background: badgeStyle.bg,
                                  color: badgeStyle.color,
                                  border: `1px solid ${badgeStyle.border}`,
                                  padding: "2px 10px",
                                  borderRadius: "20px",
                                  fontSize: "11px",
                                  fontWeight: "500",
                                }}
                              >
                                {country.focus_type_label}
                              </span>
                            </div>
                            
                            {country.notes && (
                              <div
                                style={{
                                  fontSize: "13px",
                                  color: "#555",
                                  margin: "6px 0",
                                  background: "#f9f9f9",
                                  padding: "6px 10px",
                                  borderRadius: "4px",
                                  borderLeft: "3px solid #ccc",
                                }}
                              >
                                <em>📝 {country.notes}</em>
                              </div>
                            )}
                            
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#888",
                                marginTop: "6px",
                                display: "flex",
                                gap: "15px",
                              }}
                            >
                              <span>Assigned: {new Date(country.assigned_at).toLocaleDateString()}</span>
                              <span>Updated: {new Date(country.updated_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p>
                  <strong>{assignedFocusCountries.length} focus countr{assignedFocusCountries.length === 1 ? 'y' : 'ies'} assigned</strong>
                </p>
              )}
            </div>
          </div>
        )}

        
      </div>
    </div>
  );
}

export default AgentDetails;